/**
 * This service allows components displayed in various ui-views
 * to configure the content of the top toolbar.
 * 
 * When accessing this service, a component must pass its angular scope,
 * so that we can remove the elements when the scope is destroyed,
 * instead of relying on every components to clean up the top toolbar manually.
 * 
 * The 'config' data structure is retrieved by the topToolbar directive
 * and can have the following elements:
 * 
    {
      'sideNavOpener': {
        'show': <boolean>
      },      
      'subNavBurger': {
        'show': <boolean> // not used by the html (ng-if uses !!subNavBurger ),
      },
      'companyName': {
        'text': <string>,
      },
      'firstLevelNav': {
        'text': <string>,
      },
      'title': {
        'text': <string>,
      },
      'titleIconButton': {
        'icon': <string>,
        'clickFn': <function>,
      },
      'backButtonWithText': {
        'text': <string>,
        'clickFn': <function>,
      },
      'goBackLink': {
        'text': <string>,
        'uisref': <function>,
      },      
      'tooltip': {
        'text': <string>,
      },
      'switch': {
        'text': <string>,
        'value': <boolean>,
        'changeFn': <function>,
        'disabled': <boolean>,
      },      
      'button': {
        'text': <string>,
        'clickFn': <function>,
        'disabled': <boolean>,
      },      
      'secondaryButton': {
        'text': <string>,
        'clickFn': <function>,
        'disabled': <boolean>,
      },
      'dropDownButton': {
        'dropDownButtonText': <string>, 
        'items': [list of items {'text':<string>, 'clickFn': <fucntion>}], 
      },
      'svgIconButton': {
        'iconPath': <string>,
        'clickFn': <function>,
        'buttonCssClass': <string>,
        'tooltipKey': <string>,
        'tooltipCssClass': <string>
      },      
      'extraClass': {
        'value': <string>
      },
      'tppTodayCounters': {
        'answered': <number>,
        'archived': <number>,
      }
    }
 * 
 * In addition, this service adds a 'ownerScope' property to each config element,
 * to keep track of who set what in the structure and automatically remove elements from the toolabr
 */
export class TopToolbarService {

  constructor($translate) {

    this.$translate = $translate;

    // The 'config' property must not be reassigned, as its sub-properties are watched by the directive
    this.config = {
      'sideNavOpener': null,
      'subNavBurger': null,
      'companyName': null,
      'firstLevelNav': null,
      'title': null,
      'titleIconButton': null,
      'backButtonWithText': null,
      'goBackLink': null,
      'tooltip': null,
      'switch': null,
      'button': null,
      'secondaryButton': null,
      'dropDownButton': null,
      'svgIconButton': null,
      'extraClass': null,
      'tppTodayCounters': null,
      'pendingMessagesCounter': 0, // The stock counter is set separately, by the nav controllers (inbox / pushbox)
      'warnAdminUser': false  // warn logged user of their administration rights
    };

    this.expandSideNavFn = null;
  }

  getConfig() {
    return this.config;
  }

  _newConfigElement(scope) {
    return {
      'ownerScope': scope
    };
  }

  /**
   * If the given element is null or is owned by a scope other than the given scope, 
   * clears the element and assign the given scope as its owner.
   * In any case, updates the given property of the given element to the given value
   */
  _updateConfigElement(elementName, propertyName, value, scope) {
    if (this.config[elementName] === null || (scope && this.config[elementName].ownerScope !== scope)) {
      this.config[elementName] = this._newConfigElement(scope);
      if (scope) {
        scope.$on('$destroy', () => this._clearConfigElementIfOwner(elementName, scope));
      }
    }
    this.config[elementName][propertyName] = value;
  }

  /**
   * Removes the corresponding element from the config,
   * if it has not been updated by another scope in the meantime
   */
  _clearConfigElementIfOwner(elementName, scope) {
    if (this.config[elementName] && (!scope || this.config[elementName].ownerScope === scope)) {
      this.config[elementName] = null;
    } else if (this.config[elementName]) {
      console.warn('TopToolBarService: not clearing ' + elementName + ' as the owner scope has changed');
    }
  }

  /**
   * Removes the corresponding element from the config,
   * without considering the ower scope
   */
  _clearConfigElement(elementName) {
    if (this.config[elementName]) {
      this.config[elementName] = null;
    }
  }

  clearAllConfigElement() {
    this._clearConfigElement('subNavBurger');
    this._clearConfigElement('tppTodayCounters');
    this._clearConfigElement('firstLevelNav');
    this._clearConfigElement('button');
    this._clearConfigElement('secondaryButton');
    this._clearConfigElement('dropDownButton');
  }

  setSideNavOpener(value, scope) {
    this._updateConfigElement('sideNavOpener', 'show', value, scope);
  }

  setSubNavBurger(scope) {
    this._updateConfigElement('subNavBurger', 'show', true, scope);
  }

  setCompanyName(value, scope) {
    this._updateConfigElement('companyName', 'text', value, scope);
  }

  setFirstLevelNavTranslation(key, scope, clickFn) {
    this.clearTitle();
    this._updateConfigElement('firstLevelNav', 'text', this.$translate.instant(key), scope);
    if (clickFn) {
      this._updateConfigElement('firstLevelNav', 'clickFn', clickFn, scope);
    }
  }

  clearFirstLevelNavTranslation(scope) {
    this._clearConfigElementIfOwner('firstLevelNav', scope);
  }

  setTitle(value, scope, options) {
    this._updateConfigElement('title', 'text', value, scope);
    if (options?.uiSref) {
      const { uiSref } = options;
      this._updateConfigElement('title', 'uiSref', uiSref, scope);
    }
  }

  setTitleTranslation(key, scope, options) {
    this.setTitle(this.$translate.instant(key), scope, options);
  }

  clearTitle(scope) {
    this._clearConfigElementIfOwner('title', scope);
  }

  setTitleIconButton(icon, clickFn, scope) {
    this._updateConfigElement('titleIconButton', 'icon', icon, scope);
    this._updateConfigElement('titleIconButton', 'clickFn', clickFn, scope);
  }

  // this button is at the right of the toolbar, and takes a path to an svg file (as opposed to a material icon name)
  setSVGIconButton(iconPath, clickFn, buttonCssClass, tooltipKey, tooltipCssClass, scope) {
    this._updateConfigElement('svgIconButton', 'iconPath', iconPath, scope);
    this._updateConfigElement('svgIconButton', 'clickFn', clickFn, scope);
    this._updateConfigElement('svgIconButton', 'buttonCssClass', buttonCssClass, scope);
    this._updateConfigElement('svgIconButton', 'tooltipKey', tooltipKey, scope);
    this._updateConfigElement('svgIconButton', 'tooltipCssClass', tooltipCssClass, scope);
  }

  setBackButtonWithText(textKey, clickFn, scope) {
    this._updateConfigElement('backButtonWithText', 'text', this.$translate.instant(textKey), scope);
    this._updateConfigElement('backButtonWithText', 'clickFn', clickFn, scope);
  }

  setGoBackLink(textKey, uisref, scope) {
    this._updateConfigElement('goBackLink', 'text', this.$translate.instant(textKey), scope);
    this._updateConfigElement('goBackLink', 'uisref', uisref, scope);
  }

  setTooltip(value, scope) {
    this._updateConfigElement('tooltip', 'text', value, scope);
  }

  setSwitch(switchTextKey, value, changeFn, scope, testId) {
    this._updateConfigElement('switch', 'text', this.$translate.instant(switchTextKey), scope);
    this._updateConfigElement('switch', 'value', !!value, scope);
    this._updateConfigElement('switch', 'changeFn', changeFn, scope);
    this._updateConfigElement('switch', 'disabled', false, scope);
    if (testId) {
      this._updateConfigElement('switch', 'testId', testId, scope);
    }
  }

  setButton(buttonTextKey, clickFn, scope = { $on: () => { }, saveBtnState: 0 }, testId) {
    this._updateConfigElement('button', 'text', this.$translate.instant(buttonTextKey), scope);
    this._updateConfigElement('button', 'clickFn', clickFn, scope);
    this._updateConfigElement('button', 'disabled', false, scope);
    if (testId) {
      this._updateConfigElement('button', 'testId', testId, scope);
    }
  }

  updateButtonText(buttonTextKey, scope) {
    this._updateConfigElement('button', 'text', this.$translate.instant(buttonTextKey), scope);
  }

  updateButtonDisable(isDisabled, scope) {
    this._updateConfigElement('button', 'disabled', isDisabled, scope);
  }

  clearButton(scope) {
    this._clearConfigElementIfOwner('button', scope);
  }

  setSecondaryButton(buttonTextKey, clickFn, scope, options) {

    this._updateConfigElement('secondaryButton', 'text', this.$translate.instant(buttonTextKey), scope);
    this._updateConfigElement('secondaryButton', 'clickFn', clickFn, scope);
    this._updateConfigElement('secondaryButton', 'disabled', false, scope);
    if (options) {
      const { iconPath, testId } = options;
      if (iconPath) {
        this._updateConfigElement('secondaryButton', 'iconPath', iconPath, scope);
      }
      if (testId) {
        this._updateConfigElement('secondaryButton', 'testId', testId, scope);
      }
    }
  }

  /**
   * Utility function used to clean the buttons of angular component while the hook onDestroy is called (from the component).
   */
  clearButtons() {
    this._clearConfigElementIfOwner('button', null);
    this._clearConfigElementIfOwner('secondaryButton', null);
  }

  clearSecondaryButton(scope) {
    this._clearConfigElementIfOwner('secondaryButton', scope);
  }

  updateSecondaryButtonDisable(isDisabled, scope) {
    this._updateConfigElement('secondaryButton', 'disabled', isDisabled, scope);
  }

  updateSecondaryButtonIcon(options) {
    const scope = null;
    if (options) {
      const { iconPath } = options;
      if (iconPath) {
        this._updateConfigElement('secondaryButton', 'iconPath', iconPath, scope);
      } else {
        this._updateConfigElement('secondaryButton', 'iconPath', null, scope);
      }
    }
  }

  setDropDownButton(dropDownButtonTextKey, items, scope) {
    this._updateConfigElement('dropDownButton', 'dropDownButtonText', this.$translate.instant(dropDownButtonTextKey), scope);
    this._updateConfigElement('dropDownButton', 'items', items, scope);
  }

  clearDropDownButton(scope) {
    this._clearConfigElementIfOwner('dropDownButton', scope);
  }

  setExpandSideNavFn(fn) {
    this.expandSideNavFn = fn;
  }

  callExpandSideNavFn() {
    if (this.expandSideNavFn) {
      this.expandSideNavFn();
    }
  }

  setExtraClass(value, scope) {
    if (this.config.warnAdminUser) {
      value = 'warn-admin-user';
    }
    this._updateConfigElement('extraClass', 'value', value, scope);
  }

  setWarnAdminUser(value) {
    this.config.warnAdminUser = value;
  }

  clearExtraClass(scope) {
    this._clearConfigElementIfOwner('extraClass', scope);
  }

  setTppTodayCounters(answered, archived, assigned, scope) {
    this._updateConfigElement('tppTodayCounters', 'answered', answered, scope);
    this._updateConfigElement('tppTodayCounters', 'archived', archived, scope);
    this._updateConfigElement('tppTodayCounters', 'assigned', assigned, scope);
  }

  clearTppTodayCounters(scope) {
    this._clearConfigElementIfOwner('tppTodayCounters', scope);
  }

  setPendingMessagesCounter(counter) {
    // this is done without checking scope, as it is called by a nav controller,
    // and that nav controller doesn't have a reference to the main page's controller
    this.config.pendingMessagesCounter = counter;
  }

  static TopToolbarFactory($translate, $rootScope, $state, ConfirmationFactory) {
    const topToolbarService = new TopToolbarService($translate);
    const config = topToolbarService.getConfig();

    $rootScope.$on('$stateChangeStart',
      function (event, toState, toParams, fromState, fromParams, options) {
        const isPreferencePageReloading = fromState
          && fromState.name === 'auth.alcmeon.fuse.preferences.account'
          && toState
          && toState.name === fromState.name;

        if ((config.button
          && config.button.text.toLowerCase() === $translate.instant('save').toLowerCase()
          && config.button.disabled === false
          && !isPreferencePageReloading)
          ||
          (config.button?.ownerScope?.saveBtnState === 1)) {  // Extra check for pages with two tabs and different top right btn
          //  transitionTo() promise will be rejected with a 'transition prevented' error
          event.preventDefault();
          ConfirmationFactory.materialConfirmDialog('warning/unsaved_changes')
            .then(() => {
              topToolbarService.clearButton(config.button.ownerScope);
              $state.go(toState, toParams);
            });
        }
      });
    return topToolbarService;
  }
}
TopToolbarService.TopToolbarFactory.$inject = ['$translate', '$rootScope', '$state', 'ConfirmationFactory'];
